import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Tag, Modal, message, Space } from "antd";
import { FilterTwoTone } from "@ant-design/icons";
import type { ColumnsType } from "antd/lib/table";
import PageTitle from "@component/PageTitle";
import IntegratedTable from "@component/Table/IntegratedTable";
import SCMContractApi, { SCMContract, SCMContractListParams } from "@api/scmAPI";
import timeUtils from "@utils/timeUtils";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/store";
import { RootState } from "@redux/rootReducer";
import { fetchContractList } from "@redux/SCMSlice";
import moment from "moment";
import CreateContractModal from "./CreateContractModal";
import { CONTRACT_MODE_TEXT, CONTRACT_STATUS, CONTRACT_STATUS_TEXT, getStatusColor } from "./utils";
import Filter from "./Filter";
import SCMContractViewModal from "./SCMContractViewModal";

const Wrapper = styled.div`
  padding: 20px 20px 20px 25px;
`;

export default function SCMContractPage() {
  const dispatch = useAppDispatch();
  const { isFetching } = useSelector((state: RootState) => state.SCMSlice);
  const [dataSource, setDataSource] = useState<SCMContract[]>([]);
  const [total, setTotal] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "copy">("create");
  const [editingContractId, setEditingContractId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filterParams, setFilterParams] = useState<SCMContractListParams>({
    limit: 20,
    offset: 0,
  });

  // 取得合約列表
  const fetchContractListData = useCallback(
    async (params: SCMContractListParams) => {
      try {
        const response = await dispatch(fetchContractList(params)).unwrap();
        setDataSource(response.results);
        setTotal(response.count);
      } catch (error) {
        message.error("取得合約列表失敗");
      }
    },
    [dispatch],
  );

  // 處理篩選提交
  const handleFilterSubmit = (values: any) => {
    const params: SCMContractListParams = {
      limit: filterParams.limit,
      offset: 0, // 重置到第一頁
      ubn: values.ubn,
      name: values.name,
      status: values.status,
      contractMode: values.contractMode,
      startDateBefore: values.startDate ? timeUtils.formatToTimestamp(values.startDate[1], "YYYY-MM-DD") : undefined,
      startDateAfter: values.startDate ? timeUtils.formatToTimestamp(values.startDate[0], "YYYY-MM-DD") : undefined,
    };
    setFilterParams(params);
  };

  const handleFilterReset = () => {
    setFilterParams({
      limit: 20,
      offset: 0,
    });
  };

  const updateFilterParams = (params: SCMContractListParams) => {
    setFilterParams(params);
  };

  const handleEdit = (record: SCMContract) => {
    setEditingContractId(record.id);
    setModalMode("edit");
    setShowModal(true);
  };

  const handleCopy = (record: SCMContract) => {
    setEditingContractId(record.id);
    setModalMode("copy");
    setShowModal(true);
  };

  const handleHistory = (record: SCMContract) => {
    window.open(`/scm-contract/${record.ubn}`, "_blank");
  };

  const handleDisable = (record: SCMContract) => {
    let content = "";
    if (record.status === CONTRACT_STATUS.ACTIVE) {
      content = `合約結束日將於明日凌晨（${moment().add(1, "day").format("YYYY-MM-DD")}）生效`;
    }
    Modal.confirm({
      title: "確定要停用此合約嗎？",
      content,
      okText: "確認",
      cancelText: "取消",
      async onOk() {
        try {
          await SCMContractApi.disableSCMContract(record.id);
          message.success("已停用合約");
          fetchContractListData(filterParams);
        } catch (error) {
          message.error("操作失敗，請稍後再試");
        }
      },
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: "統一編號",
      dataIndex: "ubn",
      key: "ubn",
      width: 120,
    },
    {
      title: "廠商名稱",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (text: string, record: SCMContract) => (
        <Button type="link" size="small" onClick={() => setSelectedId(record.id)}>
          {text}
        </Button>
      ),
    },
    {
      title: "合作類型",
      dataIndex: "contractMode",
      key: "contractMode",
      width: 120,
      align: "center",
      render: (text: number) => CONTRACT_MODE_TEXT[text] || "-",
    },
    {
      title: "負責人",
      dataIndex: "staffName",
      key: "staffName",
      align: "center",
      width: 100,
    },
    {
      title: "狀態",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: number) => {
        return <Tag color={getStatusColor(status)}>{CONTRACT_STATUS_TEXT[status] || "-"}</Tag>;
      },
    },
    {
      title: "生效期間",
      key: "effectivePeriod",
      width: 220,
      render: (record: SCMContract) => {
        const startDate = record.startDate ? timeUtils.formatToTimestamp(record.startDate, "YYYY-MM-DD") : "";
        const endDate = record.endDate ? timeUtils.formatToTimestamp(record.endDate, "YYYY-MM-DD") : "";
        return `${startDate} - ${endDate}`;
      },
    },
    {
      title: "建立時間",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (text: string) => (text ? timeUtils.formatToTimestamp(text, "YYYY-MM-DD HH:mm:ss") : "-"),
    },
    {
      title: "最後更新者",
      dataIndex: "lastUpdatedBy",
      key: "lastUpdatedBy",
      align: "center",
      width: 120,
    },
    {
      title: "操作",
      key: "action",
      width: 210,
      fixed: "right" as const,
      render: (record: SCMContract) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => handleEdit(record)}
            disabled={record.status === CONTRACT_STATUS.ENDED || record.status === CONTRACT_STATUS.DISABLED}
          >
            編輯
          </Button>
          {record.contractMode && (
            <>
              <Button
                type="link"
                size="small"
                onClick={() => handleCopy(record)}
                disabled={record.status === CONTRACT_STATUS.INACTIVE}
              >
                複製
              </Button>
              <Button type="link" size="small" onClick={() => handleHistory(record)}>
                歷史
              </Button>
              <Button
                type="link"
                size="small"
                onClick={() => handleDisable(record)}
                disabled={!(record.status === CONTRACT_STATUS.ACTIVE || record.status === CONTRACT_STATUS.INACTIVE)}
              >
                停用
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 監聽 filterParams 變化
  useEffect(() => {
    fetchContractListData(filterParams);
  }, [filterParams, fetchContractListData]);

  return (
    <Wrapper>
      <PageTitle title="廠商合約" />
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
        <Button icon={<FilterTwoTone />} onClick={() => setShowFilter(!showFilter)}>
          篩選
        </Button>
        <Button
          type="primary"
          onClick={() => {
            setModalMode("create");
            setEditingContractId(null);
            setShowModal(true);
          }}
        >
          新增合約
        </Button>
      </div>
      <Filter onSubmit={handleFilterSubmit} onReset={handleFilterReset} isVisible={showFilter} />
      <IntegratedTable
        isPagination
        paginationConfig={{
          totalCount: total,
          updateFilterParams,
          filterParams,
        }}
        loading={isFetching}
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        scroll={{ x: 1400 }}
      />
      {showModal && (
        <CreateContractModal
          visible={showModal}
          onCancel={() => {
            setShowModal(false);
            setEditingContractId(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingContractId(null);
            fetchContractListData(filterParams);
          }}
          mode={modalMode}
          contractId={editingContractId ?? undefined}
        />
      )}
      {!!selectedId && (
        <SCMContractViewModal visible={!!selectedId} contractId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </Wrapper>
  );
}
